import type { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	computed,
	inject,
	input,
	signal,
	viewChild,
} from '@angular/core';
import type { NgForm } from '@angular/forms';
import { FormGroup } from '@angular/forms';

import type { FormlyFormOptions } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { RecaptchaNotificationComponent } from '@valk-nx/components/ui-recaptcha-notification/src/lib/recaptcha-notification';
import { HOTEL_SLUG } from '@valk-nx/core/lib/core';
import type { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import type {
	HotelRecipients,
	Recipients,
} from '@valk-nx/services/form/src/lib/form.service';
import { FormService } from '@valk-nx/services/form/src/lib/form.service';
import { RecaptchaFormService } from '@valk-nx/services/recaptcha-form/src/lib/recaptcha-form.service';
import { shouldUseRecaptchaV2 } from '@valk-nx/services/recaptcha-form/src/lib/recaptcha-utils';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	FormInputInterface,
	FormSelectInterface,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

interface ParsedFormField {
	id: string;
	label: string;
	value: string | CheckedOption[];
}

@Component({
	selector: 'sb-form',
	templateUrl: './form.html',
	imports: [
		AlertComponent,
		TranslatePipe,
		RecaptchaNotificationComponent,
		StoryblokDirective,
		SharedFormlyModule,
	],
	host: { class: 'w-full' },
})
export class FormComponent extends StoryblokRootDirective {
	private readonly formService = inject(FormService);
	private readonly recaptchaFormService = inject(RecaptchaFormService);
	readonly hotelSlug = inject(HOTEL_SLUG);

	subject = input.required<string>();
	recipient = input<Recipients>();
	hotelRecipients = input<HotelRecipients | undefined>();
	blocks = input.required<(FormInputInterface & FormSelectInterface)[]>();
	buttonText = input.required<string>();
	successTitle = input.required<string>();
	successContent = input.required<string>();

	readonly formRef = viewChild.required<NgForm>('formRef');

	readonly useRecaptchaV2 = signal(false);
	readonly requestSubmitted = signal(false);
	readonly requestSubmitting = signal(false);
	readonly parsedFormData = signal<ParsedFormField[]>([]);

	readonly subjectTestId = computed(() =>
		StoryblokHelper.resolveSubjectPlaceholders(
			this.subject(),
			[],
		).toLowerCase(),
	);
	readonly blocksWithId = computed(() =>
		this.blocks().map((block) => ({ ...block, id: block._uid })),
	);
	private readonly storyblokFormFields = computed(() =>
		StoryblokHelper.buildFormlyData(this.blocksWithId()),
	);
	readonly formFields = computed(() =>
		this.recaptchaFormService.addRecaptchaToForm(
			this.storyblokFormFields(),
			this.useRecaptchaV2(),
		),
	);

	formGroup = new FormGroup({});
	options: FormlyFormOptions = {};
	model = {};

	async onSubmit(): Promise<void> {
		if (!this.formGroup.valid) {
			GeneralHelper.scrollToElementWithClass(
				'form-field-error',
				undefined,
				'input',
			);
			return;
		}

		this.requestSubmitting.set(true);
		const parsedFormData = StoryblokHelper.parsedFormData(
			this.formGroup.value,
			this.blocksWithId(),
			this.language(),
		).filter((formField) => !!formField);
		this.parsedFormData.set(parsedFormData);

		const emailRecipients = StoryblokHelper.parseEmailRecipientFormData(
			this.blocksWithId(),
			parsedFormData,
		);

		return this.formService
			.submitCustomForm(
				StoryblokHelper.resolveSubjectPlaceholders(
					this.subject(),
					parsedFormData,
				),
				parsedFormData,
				this.recipient(),
				this.hotelRecipients(),
				this.hotelSlug,
				emailRecipients,
				this.formGroup.get('token')?.value,
			)
			.then((response) => {
				this.requestSubmitting.set(false);
				this.requestSubmitted.set(response.success);
			})
			.catch((err: HttpErrorResponse) => {
				this.requestSubmitting.set(false);
				if (shouldUseRecaptchaV2(err)) {
					this.useRecaptchaV2.set(true);
				}
			});
	}

	resetForm(): void {
		this.requestSubmitted.set(false);
		this.requestSubmitting.set(false);

		this.formGroup.reset();
		this.formRef().resetForm();
	}
}
